/**
 * Seeded, rotationally symmetric map generation.
 *
 * Symmetry is fairness — every cell `(x, y)` matches `(S-1-x, S-1-y)` in
 * terrain class and regolith amount — and it is also a cheap invariant to
 * assert, which is why the generator builds only the canonical half and
 * mirrors it rather than generating twice and hoping.
 *
 * Hand-authoring a map would be asset work by another name (A-7).
 */

import { Rng } from '../core/rng.js'
import * as fix from '../core/fixed.js'
import { World, CELL, TERRAIN, REGOLITH_FULL, LEVELS } from './world.js'
import { dressLandscape, layRoads } from './landscape.js'

/** Cells around a start position kept flat, clear, and buildable. */
const START_CLEAR_RADIUS = 13
/** Every start must have a field no further than this, in cells. */
export const MAX_START_FIELD_DISTANCE = 24

export interface MapGenOptions {
  seed: number | string
  playerCount: number
  /** 0..1 in Q16.16. Higher pushes the elevation thresholds up, so less relief. */
  cliffDensity?: fix.Fixed
  fieldCount?: number
  /** Rich gem patches per half. Rare on purpose; see the placement comment. */
  gemCount?: number
}

/**
 * One in this many wall cells becomes a ramp.
 *
 * Ramps are what make a plateau a place rather than an obstacle, and the count
 * is a gameplay dial: too few and high ground is scenery, too many and the wall
 * stops meaning anything. One in seven leaves a plateau with a handful of ways
 * up, which is enough for it to be worth holding and possible to flank.
 */
const RAMP_RARITY = 7

/** Below this many cells, an unreachable pocket is raised rather than joined. */
const MIN_POCKET = 10

const NEIGHBOURS: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
]

export function generateMap(world: World, opts: MapGenOptions): void {
  const size = world.size
  const rng = new Rng(opts.seed)
  world.reset()

  // The fbm output clusters around 0.5, so the threshold sits well above the
  // midpoint: at 0.42 more than half the map came out as cliff and the result
  // read as a maze rather than a landscape.
  const cliffDensity = opts.cliffDensity ?? fix.fromFloat(0.68)
  const noiseSeed = rng.next() | 0
  const detailSeed = rng.next() | 0
  const rampSeed = rng.next() | 0
  const basinSeed = rng.next() | 0

  // --- 1. elevation and terrain, built on the canonical half only ---------
  //
  // The map is a set of stacked plateaus rather than a field of obstacles. The
  // same noise that used to answer "is this cell a cliff?" now answers "which
  // level is this cell on", and the walls fall out afterwards as a derivation:
  // a cell is a wall exactly when it looks down on a neighbour. That inversion
  // is what buys real relief without the pathfinder learning anything new —
  // wall cells are impassable, which it already understands.
  const mirror = (i: number): number => size * size - 1 - i

  // Thresholds ride on `cliffDensity` so the one existing dial still means
  // "how much relief". The fbm output clusters hard around 0.5, so these sit
  // close together; spread them out and the map comes back as a single plateau.
  const highBand = cliffDensity
  const midBand = (cliffDensity - fix.fromFloat(0.09)) as fix.Fixed
  const basinBand = fix.fromFloat(0.33)

  for (let cy = 0; cy < size; cy++) {
    for (let cx = 0; cx < size; cx++) {
      const i = cy * size + cx
      const m = mirror(i)
      if (m < i) {
        world.height[i] = world.height[m]
        world.flags[i] = world.flags[m]
        continue
      }

      const elevation = fbm(cx, cy, noiseSeed, 3, 22)
      const level = elevation > highBand ? 2 : elevation > midBand ? 1 : 0
      // Basins come from their own low-frequency field rather than from the
      // bottom of the elevation range. Thresholding one noise into three bands
      // and a lake puts the lake wherever the elevation happens to dip, which
      // is everywhere, in single-cell flecks. A separate broad field puts it in
      // a few places, in one piece.
      const basin = level === 0 && fbm(cx, cy, basinSeed, 2, 24) < basinBand

      world.height[i] = level
      world.flags[i] = basin ? CELL.Water : CELL.Passable
      world.height[m] = world.height[i]
      world.flags[m] = world.flags[i]
    }
  }

  // Two smoothing passes over the quantized field. Thresholded noise produces
  // ragged single-cell islands, and at this zoom those read as speckle rather
  // than as terrain — worse, each one becomes a wall the moment the levels are
  // derived, so the speckle is also in the pathfinding. A majority filter is
  // symmetric under the map's own 180-degree rotation, so it cannot break the
  // fairness invariant.
  smoothLevels(world)
  smoothLevels(world)
  smoothWater(world)

  // Terrain class follows elevation, the shoreline, and a detail noise — in
  // that order of priority, so that a material always agrees with the shape it
  // is painted on.
  for (let cy = 0; cy < size; cy++) {
    for (let cx = 0; cx < size; cx++) {
      const i = cy * size + cx
      const m = mirror(i)
      // The detail noise is not symmetric under the rotation, so this loop
      // stays on the canonical half like the elevation loop above.
      if (m < i) {
        world.terrain[i] = world.terrain[m]
        world.height[i] = world.height[m]
        world.flags[i] = world.flags[m]
        continue
      }
      const border = cx === 0 || cy === 0 || cx === size - 1 || cy === size - 1
      if (border) {
        world.height[i] = LEVELS - 1
        world.flags[i] = CELL.Cliff
        world.terrain[i] = TERRAIN.Rock
        copyToMirror(world, i, m)
        continue
      }
      if ((world.flags[i] & CELL.Water) !== 0) {
        world.terrain[i] = TERRAIN.Ice
        copyToMirror(world, i, m)
        continue
      }
      const level = world.height[i]
      if (level === 2) {
        world.terrain[i] = TERRAIN.Rock
      } else if (level === 1) {
        world.terrain[i] = TERRAIN.Basalt
      } else if (nearWater(world, cx, cy, 2)) {
        // Terraforming follows the melt. A green fringe only reads as growth
        // if it hugs the ice; scattered across the map it reads as a third
        // kind of dirt.
        world.terrain[i] = TERRAIN.Verdant
      } else {
        const detail = fbm(cx, cy, detailSeed, 2, 9)
        world.terrain[i] =
          detail > fix.fromFloat(0.62)
            ? TERRAIN.Basalt
            : detail < fix.fromFloat(0.3)
              ? TERRAIN.Crater
              : TERRAIN.Dust
      }
      copyToMirror(world, i, m)
    }
  }

  // --- 2. start positions --------------------------------------------------
  const starts = chooseStarts(size, opts.playerCount)
  for (let p = 0; p < starts.length; p++) {
    world.starts[p * 2] = starts[p][0]
    world.starts[p * 2 + 1] = starts[p][1]
    clearArea(world, starts[p][0], starts[p][1], START_CLEAR_RADIUS)
  }
  layRoads(world)

  // --- 3. regolith fields --------------------------------------------------
  // One guaranteed field per start, at a mirrored offset, then scattered
  // extras. "Guaranteed" is what makes the reachability invariant hold by
  // construction rather than by retry.
  // Starts come in mirrored pairs, so the field is computed once per pair and
  // mirrored. Deriving it independently for each start does not survive the
  // arithmetic: the centre of 180-degree symmetry is at (size-1)/2, which is a
  // half-cell, and the truncating shift rounds the two offsets differently.
  const fieldCount = opts.fieldCount ?? 10
  const dist = 9
  for (let p = 0; p < starts.length; p += 2) {
    const [sx, sy] = starts[p]
    const toCenter = fix.atan2(fix.fromInt(size - 1 - 2 * sy), fix.fromInt(size - 1 - 2 * sx))
    const fx = clampCell(sx + step(fix.cos(toCenter), dist), size)
    const fy = clampCell(sy + step(fix.sin(toCenter), dist), size)
    placeField(world, fx, fy, 4, REGOLITH_FULL)
    placeField(world, size - 1 - fx, size - 1 - fy, 4, REGOLITH_FULL)
  }

  const half = (size * size) >> 1
  for (let n = 0; n < fieldCount; n++) {
    const i = rng.int(half)
    const cx = i % size
    const cy = (i / size) | 0
    const radius = rng.range(3, 6)
    placeField(world, cx, cy, radius, REGOLITH_FULL)
    // Mirror it, so both halves hold identical resources.
    placeField(world, size - 1 - cx, size - 1 - cy, radius, REGOLITH_FULL)
  }

  // Gems: a couple of small rich patches, seeded *inside* existing ore.
  //
  // Small and few on purpose. The point of a richer ore is that it is worth
  // fighting over, and that only works if there is not enough of it to go
  // round — a gem patch beside each base is just a faster economy for both
  // players, which changes the numbers and not the game.
  //
  // Placed by converting cells of a field that already exists rather than by
  // picking a random spot and hoping. A patch this small has about a dozen
  // candidate cells, and `placeField` refuses water, high ground and anything
  // already richer, so blind attempts failed silently and often: on some seeds
  // the map came out with no gems on it at all, and nothing said so. Ore has
  // already been placed on ground that satisfies every one of those rules, and
  // it is already mirrored, so seeding from it cannot fail and cannot break
  // the symmetry of the map.
  const gemCount = opts.gemCount ?? 2
  const oreCells: number[] = []
  for (let i = 0; i < half; i++) {
    if (world.terrain[i] === TERRAIN.Regolith && world.regolith[i] > 0) oreCells.push(i)
  }
  for (let n = 0; n < gemCount && oreCells.length > 0; n++) {
    // Spread the patches across the available fields rather than clustering
    // them: two gem patches in one corner is one gem patch as far as the map
    // is concerned.
    const pick = oreCells[((n * 2 + 1) * oreCells.length) / (gemCount * 2) | 0]
    const gx = pick % size
    const gy = (pick / size) | 0
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        for (const [x, y] of [
          [gx + dx, gy + dy],
          [size - 1 - gx - dx, size - 1 - gy - dy],
        ]) {
          if (!world.inBounds(x, y)) continue
          const i = y * size + x
          if (world.terrain[i] !== TERRAIN.Regolith || world.regolith[i] <= 0) continue
          world.terrain[i] = TERRAIN.Gems
        }
      }
    }
  }

  // --- 4. walls and ramps --------------------------------------------------
  // Derived, not authored, and derived *after* every mutation above: clearing a
  // start bowl or dropping a field changes which cells look down on a
  // neighbour, and a wall set before those edits would disagree with the
  // elevation it is supposed to be describing.
  markRamps(world, rampSeed)
  deriveWalls(world)

  // --- 5. connectivity -----------------------------------------------------
  // Carve a corridor from each start to the nearest field it cannot reach.
  // A start walled off from its own regolith is an unwinnable match, and it is
  // exactly the failure a human would only find twenty minutes in.
  for (let p = 0; p < starts.length; p++) {
    ensureFieldReachable(world, starts[p][0], starts[p][1])
  }
  // Starts must also be able to reach each other, or the match cannot resolve.
  for (let p = 1; p < starts.length; p++) {
    ensureConnected(world, starts[0][0], starts[0][1], starts[p][0], starts[p][1])
  }
  // Carving lowers cells, which changes which of their neighbours are looking
  // down on something. Re-derive so the walls describe the final elevation.
  deriveWalls(world)

  // --- 6. one world, not several -------------------------------------------
  ensureSingleRegion(world)
  dressLandscape(world, opts.seed)
}

/**
 * Every passable cell must be reachable from every other one.
 *
 * Elevation made this a real problem rather than a theoretical one. A plateau
 * top with no ramp, or a stretch of shore cut off by a lake, is passable ground
 * that nothing can walk to — and a *structure* can end up standing on it, at
 * which point the match cannot end: probing found matches sitting at
 * the tick cap with one side holding 142 units and the other none, unable to
 * reach the last of them. Twenty-three percent of matches ended that way.
 *
 * Pockets are repaired rather than deleted. A pocket is somewhere to fight over
 * once it has a way in, and walling it off would throw away a tenth of the map
 * to fix a connectivity bug.
 */
function ensureSingleRegion(world: World): void {
  const size = world.size
  const seen = new Uint8Array(world.cells)

  // A bounded number of passes: a repair can expose a pocket behind the one it
  // just opened, and an unbounded loop here is a hang in map generation. Six is
  // well past what any measured seed needs.
  for (let pass = 0; pass < 6; pass++) {
    const sx = world.starts[0]
    const sy = world.starts[1]
    if (sx < 0) return
    flood(world, sx, sy, seen)

    let repaired = false
    const visited = new Uint8Array(world.cells)
    for (let i = 0; i < world.cells; i++) {
      if (seen[i] !== 0 || visited[i] !== 0) continue
      if ((world.flags[i] & CELL.Passable) === 0) continue

      const pocket = collectPocket(world, i, visited)

      // A handful of cells behind a rim is a generation artefact — a dip in the
      // middle of a plateau — not a place. Filling it in is cheaper than
      // connecting it and leaves the relief intact.
      //
      // Filling means raising the *elevation*, not clearing the passable flag:
      // walls are derived from elevation, so a flag written here is overwritten
      // by the next derivation and the pocket comes straight back.
      if (pocket.length < MIN_POCKET) {
        // Every level is read before any is written. Filling in place makes the
        // result depend on iteration order, and iteration order is not
        // symmetric under the map's own rotation — the same class of bug that
        // once made overlapping ore fields asymmetric.
        const levels = pocket.map((c) => surroundingLevel(world, c))
        pocket.forEach((c, k) => fill(world, c, levels[k]))
        repaired = true
        continue
      }

      // A real pocket gets a way in. A ramp first — that is what ramps are for,
      // and it costs one cell — and only a carved corridor if the pocket is
      // walled off by something a ramp cannot cross, such as a lake.
      if (rampInto(world, pocket, seen)) {
        repaired = true
        continue
      }
      const bridge = nearestPair(world, pocket, seen)
      if (!bridge) continue
      carve(world, bridge[0] % size, (bridge[0] / size) | 0, bridge[1] % size, (bridge[1] / size) | 0)
      repaired = true
    }

    if (!repaired) break
    deriveWalls(world)
  }

  // Whatever is left is small — every pocket worth connecting has been given a
  // ramp or a corridor by now, and filling a dip repeatedly opens a smaller one
  // beside it. Fill them all, unconditionally, so the invariant is *complete*:
  // one region, every cell reachable from every other. A structure standing on
  // an unreachable cell is a match that cannot end, which is worth more than
  // the two hundred cells this costs.
  flood(world, world.starts[0], world.starts[1], seen)
  const stranded: number[] = []
  for (let i = 0; i < world.cells; i++) {
    if (seen[i] === 0 && (world.flags[i] & CELL.Passable) !== 0) stranded.push(i)
  }
  const levels = stranded.map((c) => surroundingLevel(world, c))
  stranded.forEach((c, k) => fill(world, c, levels[k]))
}

/**
 * Raise a cell into the ground around it — **and its mirror image**.
 *
 * Mirroring here is not belt-and-braces. Repairs are applied in index order
 * against a reachability map computed at the start of the pass, so opening one
 * pocket can change which branch its mirror takes half a pass later, and the
 * two sides end up with different terrain. Writing both cells makes the repair
 * symmetric whatever order it runs in, which is the same reason `carve` cuts
 * both a line and its mirror.
 */
function fill(world: World, cell: number, level: number): void {
  const mirror = world.cells - 1 - cell
  for (const c of [cell, mirror]) {
    world.height[c] = level
    world.terrain[c] = TERRAIN.Rock
    world.regolith[c] = 0
    world.flags[c] = CELL.Cliff
  }
}

/** The lowest level among a cell's higher orthogonal neighbours. */
function surroundingLevel(world: World, cell: number): number {
  const size = world.size
  const cx = cell % size
  const cy = (cell / size) | 0
  let best = world.height[cell]
  for (const [dx, dy] of NEIGHBOURS) {
    const nx = cx + dx
    const ny = cy + dy
    if (!world.inBounds(nx, ny)) continue
    const level = world.height[ny * size + nx]
    if (level > world.height[cell] && (best === world.height[cell] || level < best)) best = level
  }
  return best
}

/**
 * Cut a ramp through the rim between a pocket and the main region.
 *
 * The wall cells around a pocket are, by construction, exactly the cells that
 * look down on a neighbour — so one of them touching both sides is a slope
 * waiting to be marked, and marking it is a one-cell edit that leaves every
 * plateau standing.
 */
function rampInto(world: World, pocket: number[], seen: Uint8Array): boolean {
  const size = world.size
  const inPocket = new Set(pocket)
  const mirror = (i: number): number => size * size - 1 - i

  for (const c of pocket) {
    const cx = c % size
    const cy = (c / size) | 0
    for (const [dx, dy] of NEIGHBOURS) {
      const wx = cx + dx
      const wy = cy + dy
      if (wx < 1 || wy < 1 || wx >= size - 1 || wy >= size - 1) continue
      const w = wy * size + wx
      if ((world.flags[w] & CELL.Cliff) === 0) continue

      // Does this wall cell also touch the main region?
      for (const [ex, ey] of NEIGHBOURS) {
        const ox = wx + ex
        const oy = wy + ey
        if (ox < 0 || oy < 0 || ox >= size || oy >= size) continue
        const o = oy * size + ox
        if (seen[o] === 0 || inPocket.has(o)) continue
        world.flags[w] |= CELL.Ramp
        world.flags[mirror(w)] |= CELL.Ramp
        return true
      }
    }
  }
  return false
}

/** The closest (pocket cell, reachable cell) pair, for a carved corridor. */
function nearestPair(world: World, pocket: number[], seen: Uint8Array): [number, number] | null {
  const size = world.size
  let best: [number, number] | null = null
  let bestD = Infinity
  for (const c of pocket) {
    const cx = c % size
    const cy = (c / size) | 0
    for (let j = 0; j < world.cells; j++) {
      if (seen[j] === 0) continue
      const jx = j % size
      const jy = (j / size) | 0
      const d = (jx - cx) * (jx - cx) + (jy - cy) * (jy - cy)
      if (d < bestD) {
        bestD = d
        best = [c, j]
      }
    }
  }
  return best
}

/** Every passable cell connected to `start`, marking `visited` as it goes. */
function collectPocket(world: World, start: number, visited: Uint8Array): number[] {
  const size = world.size
  const out: number[] = []
  const stack = [start]
  visited[start] = 1
  while (stack.length > 0) {
    const i = stack.pop() as number
    out.push(i)
    const cx = i % size
    const cy = (i / size) | 0
    for (const [dx, dy] of NEIGHBOURS) {
      const nx = cx + dx
      const ny = cy + dy
      if (nx < 0 || ny < 0 || nx >= size || ny >= size) continue
      const ni = ny * size + nx
      if (visited[ni] !== 0) continue
      if ((world.flags[ni] & CELL.Passable) === 0) continue
      visited[ni] = 1
      stack.push(ni)
    }
  }
  return out
}

function copyToMirror(world: World, i: number, m: number): void {
  world.terrain[m] = world.terrain[i]
  world.height[m] = world.height[i]
  world.flags[m] = world.flags[i]
}

/** True if ice sits within `radius` cells, measured as a square. */
function nearWater(world: World, cx: number, cy: number, radius: number): boolean {
  for (let y = cy - radius; y <= cy + radius; y++) {
    for (let x = cx - radius; x <= cx + radius; x++) {
      if (!world.inBounds(x, y)) continue
      if ((world.flags[y * world.size + x] & CELL.Water) !== 0) return true
    }
  }
  return false
}

/** Majority filter over elevation levels: an island of one loses to its ring. */
function smoothLevels(world: World): void {
  const size = world.size
  const next = Uint8Array.from(world.height)
  for (let cy = 1; cy < size - 1; cy++) {
    for (let cx = 1; cx < size - 1; cx++) {
      const i = cy * size + cx
      const level = world.height[i]
      const n = [world.height[i - 1], world.height[i + 1], world.height[i - size], world.height[i + size]]
      let agree = 0
      for (const v of n) if (v === level) agree++
      if (agree >= 2) continue
      // Outvoted: take whichever level the majority of the ring is on.
      const counts = new Uint8Array(LEVELS)
      for (const v of n) counts[v]++
      let best = level
      let bestCount = 0
      for (let l = 0; l < LEVELS; l++) {
        if (counts[l] > bestCount) {
          bestCount = counts[l]
          best = l
        }
      }
      next[i] = best
    }
  }
  world.height.set(next)
}

/** The same filter for ice, so lakes have shores rather than fringes of foam. */
function smoothWater(world: World): void {
  const size = world.size
  const next = Uint8Array.from(world.flags)
  for (let cy = 1; cy < size - 1; cy++) {
    for (let cx = 1; cx < size - 1; cx++) {
      const i = cy * size + cx
      const wet = (world.flags[i] & CELL.Water) !== 0
      let neighbours = 0
      for (const j of [i - 1, i + 1, i - size, i + size]) {
        if ((world.flags[j] & CELL.Water) !== 0) neighbours++
      }
      // Ice only survives where the ground is lowest; a lake draped over a
      // plateau rim is water running uphill.
      if (wet && (neighbours < 2 || world.height[i] !== 0)) next[i] &= ~CELL.Water
      else if (!wet && neighbours === 4 && world.height[i] === 0) next[i] |= CELL.Water
    }
  }
  world.flags.set(next)
}

/**
 * A cell is a wall when it looks down on an orthogonal neighbour: it is the rim
 * of its own plateau. Ramps and water are exempt — a ramp is the way up, and
 * ice is impassable without being something you stand beside.
 */
function deriveWalls(world: World): void {
  const size = world.size
  for (let cy = 0; cy < size; cy++) {
    for (let cx = 0; cx < size; cx++) {
      const i = cy * size + cx
      if ((world.flags[i] & CELL.Water) !== 0) {
        world.flags[i] &= ~(CELL.Passable | CELL.Cliff)
        continue
      }
      if (cx === 0 || cy === 0 || cx === size - 1 || cy === size - 1) {
        world.flags[i] = CELL.Cliff
        continue
      }
      if ((world.flags[i] & CELL.Ramp) !== 0) {
        world.flags[i] = (world.flags[i] & ~CELL.Cliff) | CELL.Passable
        continue
      }
      const level = world.height[i]
      const wall =
        world.height[i - 1] < level ||
        world.height[i + 1] < level ||
        world.height[i - size] < level ||
        world.height[i + size] < level
      if (wall) world.flags[i] = (world.flags[i] & ~CELL.Passable) | CELL.Cliff
      else world.flags[i] = (world.flags[i] & ~CELL.Cliff) | CELL.Passable
    }
  }
}

/**
 * Punch ramps through the rims.
 *
 * Chosen on the canonical half and written to both cells of each mirrored pair,
 * for the same reason the terrain is: a hash of the cell coordinates is not
 * symmetric under the 180-degree rotation, so deriving each side independently
 * would hand one player ramps the other does not have.
 *
 * A ramp is only cut where the slope is clean — the cell opposite the drop sits
 * at the same level — because a ramp at a corner produces a slope that falls
 * away in two directions and reads as a hole.
 */
function markRamps(world: World, seed: number): void {
  const size = world.size
  const mirror = (i: number): number => size * size - 1 - i
  for (let cy = 1; cy < size - 1; cy++) {
    for (let cx = 1; cx < size - 1; cx++) {
      const i = cy * size + cx
      const m = mirror(i)
      if (m < i) continue
      if ((world.flags[i] & CELL.Water) !== 0) continue

      const level = world.height[i]
      if (level === 0) continue

      const west = world.height[i - 1]
      const east = world.height[i + 1]
      const north = world.height[i - size]
      const south = world.height[i + size]

      // A clean slope: exactly one axis steps down, and the cell opposite the
      // drop is level with this one.
      const alongX = (west < level && east === level) || (east < level && west === level)
      const alongY = (north < level && south === level) || (south < level && north === level)
      if (alongX === alongY) continue
      if (hash2(cx, cy, seed) % RAMP_RARITY !== 0) continue

      world.flags[i] |= CELL.Ramp
      world.flags[m] |= CELL.Ramp
    }
  }
}

// ---------------------------------------------------------------------------
// Noise
// ---------------------------------------------------------------------------

/** Integer hash. No transcendentals, no float — identical on every engine. */
function hash2(x: number, y: number, seed: number): number {
  let h = seed | 0
  h = Math.imul(h ^ (x | 0), 0x85ebca6b)
  h = Math.imul(h ^ (y | 0), 0xc2b2ae35)
  h ^= h >>> 15
  h = Math.imul(h, 0x27d4eb2f)
  h ^= h >>> 13
  return h >>> 0
}

/** Lattice value in Q16.16 over [0, 1). */
function latticeValue(x: number, y: number, seed: number): fix.Fixed {
  return (hash2(x, y, seed) >>> 16) as fix.Fixed
}

/** t*t*(3-2t), the standard smoothstep, in fixed point. */
function smoothstep(t: fix.Fixed): fix.Fixed {
  const t2 = fix.mul(t, t)
  return fix.mul(t2, fix.sub(fix.fromInt(3), fix.mul(fix.fromInt(2), t)))
}

/** Bilinear value noise on a lattice of `period` cells. */
function valueNoise(cx: number, cy: number, seed: number, period: number): fix.Fixed {
  const gx = Math.floor(cx / period)
  const gy = Math.floor(cy / period)
  const fxRaw = cx - gx * period
  const fyRaw = cy - gy * period
  const tx = smoothstep(fix.div(fix.fromInt(fxRaw), fix.fromInt(period)))
  const ty = smoothstep(fix.div(fix.fromInt(fyRaw), fix.fromInt(period)))

  const v00 = latticeValue(gx, gy, seed)
  const v10 = latticeValue(gx + 1, gy, seed)
  const v01 = latticeValue(gx, gy + 1, seed)
  const v11 = latticeValue(gx + 1, gy + 1, seed)

  const a = fix.lerp(v00, v10, tx)
  const b = fix.lerp(v01, v11, tx)
  return fix.lerp(a, b, ty)
}

/** Fractional Brownian motion: octaves at halving amplitude. */
function fbm(cx: number, cy: number, seed: number, octaves: number, period: number): fix.Fixed {
  let sum = 0
  let amp = fix.ONE as number
  let total = 0
  let p = period
  for (let o = 0; o < octaves; o++) {
    sum += fix.mul(valueNoise(cx, cy, seed + o * 7919, p < 1 ? 1 : p), amp as fix.Fixed)
    total += amp
    amp >>= 1
    p = p >> 1
  }
  return fix.div(sum as fix.Fixed, total as fix.Fixed)
}

// ---------------------------------------------------------------------------
// Placement helpers
// ---------------------------------------------------------------------------

/**
 * Scale a Q16.16 unit-vector component by a cell distance, rounding by
 * magnitude so that negating the input negates the output exactly. A plain
 * `>> 16` floors toward negative infinity and breaks that antisymmetry.
 */
function step(component: fix.Fixed, distance: number): number {
  const magnitude = ((component < 0 ? -component : component) * distance) >> 16
  return component < 0 ? -magnitude : magnitude
}

function clampCell(v: number, size: number): number {
  return v < 1 ? 1 : v > size - 2 ? size - 2 : v
}

/**
 * Start positions, always in mirrored pairs so the 180-degree symmetry holds
 * for spawn locations as well as terrain.
 */
function chooseStarts(size: number, playerCount: number): Array<[number, number]> {
  const inset = 18
  const a: Array<[number, number]> = [
    [inset, inset],
    [size - 1 - inset, size - 1 - inset],
    [size - 1 - inset, inset],
    [inset, size - 1 - inset],
  ]
  return a.slice(0, Math.max(2, Math.min(playerCount, 4)))
}

function clearArea(world: World, cx: number, cy: number, radius: number): void {
  const r2 = radius * radius
  for (let y = cy - radius; y <= cy + radius; y++) {
    for (let x = cx - radius; x <= cx + radius; x++) {
      if (!world.inBounds(x, y)) continue
      if (x === 0 || y === 0 || x === world.size - 1 || y === world.size - 1) continue
      const dx = x - cx
      const dy = y - cy
      if (dx * dx + dy * dy > r2) continue
      const i = y * world.size + x
      world.flags[i] = CELL.Passable
      world.height[i] = 0
      world.terrain[i] = TERRAIN.Dust
      world.regolith[i] = 0
    }
  }
}

/** Writes a field and returns how many cells it actually claimed. */
function placeField(
  world: World,
  cx: number,
  cy: number,
  radius: number,
  amount: number,
  kind: number = TERRAIN.Regolith,
): number {
  let claimed = 0
  const r2 = radius * radius
  for (let y = cy - radius; y <= cy + radius; y++) {
    for (let x = cx - radius; x <= cx + radius; x++) {
      if (!world.inBounds(x, y)) continue
      const dx = x - cx
      const dy = y - cy
      const d2 = dx * dx + dy * dy
      if (d2 > r2) continue
      const i = y * world.size + x
      if ((world.flags[i] & (CELL.Cliff | CELL.Water)) !== 0) continue
      // Lowlands only. A field on a plateau is reachable solely by whichever
      // ramps happened to be cut, which turns an economy into a coin flip.
      if (world.height[i] !== 0) continue
      if (world.ground[i] !== 0) continue
      // Taper toward the edge so fields do not read as discs.
      const falloff = ((r2 - d2) * amount) / r2
      const value = falloff | 0
      // Overlapping fields merge by taking the richer cell rather than the
      // last one written. Order-independence is what makes the mirrored pair
      // of placements produce identical results on both halves — a plain
      // overwrite makes the map asymmetric wherever two fields intersect.
      if (value <= 0 || value <= world.regolith[i]) continue
      world.regolith[i] = value
      world.flags[i] |= CELL.Field
      world.terrain[i] = kind
      claimed++
    }
  }
  return claimed
}

// ---------------------------------------------------------------------------
// Connectivity
// ---------------------------------------------------------------------------

/** Flood fill of passable cells from a seed, into a reusable byte grid. */
function flood(world: World, sx: number, sy: number, out: Uint8Array): void {
  out.fill(0)
  const size = world.size
  const stack = new Int32Array(world.cells)
  let top = 0
  stack[top++] = sy * size + sx
  out[sy * size + sx] = 1
  while (top > 0) {
    const i = stack[--top]
    const cx = i % size
    const cy = (i / size) | 0
    for (let d = 0; d < 4; d++) {
      const nx = cx + (d === 0 ? 1 : d === 1 ? -1 : 0)
      const ny = cy + (d === 2 ? 1 : d === 3 ? -1 : 0)
      if (nx < 0 || ny < 0 || nx >= size || ny >= size) continue
      const ni = ny * size + nx
      if (out[ni] !== 0) continue
      if ((world.flags[ni] & CELL.Passable) === 0) continue
      out[ni] = 1
      stack[top++] = ni
    }
  }
}

/**
 * Clear cliffs along a straight line, **and along its mirror image**.
 *
 * Carving is the only asymmetric mutation in the generator, so mirroring it
 * here is what keeps the symmetry invariant true by construction rather than
 * repaired afterwards. Without this, a corridor cut for one player's start
 * exists on their side of the map only.
 */
function carve(world: World, x0: number, y0: number, x1: number, y1: number): void {
  carveLine(world, x0, y0, x1, y1)
  const s = world.size - 1
  carveLine(world, s - x0, s - y0, s - x1, s - y1)
}

function carveLine(world: World, x0: number, y0: number, x1: number, y1: number): void {
  let x = x0
  let y = y0
  const dx = Math.abs(x1 - x0)
  const dy = -Math.abs(y1 - y0)
  const sx = x0 < x1 ? 1 : -1
  const sy = y0 < y1 ? 1 : -1
  let err = dx + dy
  let guard = world.cells
  for (;;) {
    for (let oy = -1; oy <= 1; oy++) {
      for (let ox = -1; ox <= 1; ox++) {
        const nx = x + ox
        const ny = y + oy
        if (!world.inBounds(nx, ny)) continue
        if (nx === 0 || ny === 0 || nx === world.size - 1 || ny === world.size - 1) continue
        const i = ny * world.size + nx
        // Everything the corridor touches drops to the ground level, walls and
        // plateau interiors alike. Clearing only the wall cells leaves a trench
        // that climbs a cliff halfway along, with nothing to climb it by.
        if (world.height[i] !== 0 || (world.flags[i] & (CELL.Cliff | CELL.Water)) !== 0) {
          world.flags[i] = CELL.Passable
          world.height[i] = 0
          world.terrain[i] = TERRAIN.Dust
          world.regolith[i] = 0
        }
      }
    }
    if ((x === x1 && y === y1) || --guard <= 0) break
    const e2 = 2 * err
    if (e2 >= dy) {
      err += dy
      x += sx
    }
    if (e2 <= dx) {
      err += dx
      y += sy
    }
  }
}

function ensureFieldReachable(world: World, sx: number, sy: number): void {
  const size = world.size
  const seen = new Uint8Array(world.cells)
  flood(world, sx, sy, seen)

  let bestI = -1
  let bestD = Infinity
  let reachableD = Infinity
  for (let i = 0; i < world.cells; i++) {
    if (world.regolith[i] <= 0) continue
    const cx = i % size
    const cy = (i / size) | 0
    const d = (cx - sx) * (cx - sx) + (cy - sy) * (cy - sy)
    if (seen[i] !== 0) {
      if (d < reachableD) reachableD = d
    } else if (d < bestD) {
      bestD = d
      bestI = i
    }
  }

  const limit = MAX_START_FIELD_DISTANCE * MAX_START_FIELD_DISTANCE
  if (reachableD <= limit) return
  if (bestI < 0) {
    // No field near enough at all: place one and its mirror, then carve to it.
    const fx = clampCell(sx + 10, size)
    const fy = clampCell(sy + 10, size)
    placeField(world, fx, fy, 4, REGOLITH_FULL)
    placeField(world, size - 1 - fx, size - 1 - fy, 4, REGOLITH_FULL)
    carve(world, sx, sy, fx, fy)
    return
  }
  carve(world, sx, sy, bestI % size, (bestI / size) | 0)
}

function ensureConnected(world: World, ax: number, ay: number, bx: number, by: number): void {
  const seen = new Uint8Array(world.cells)
  flood(world, ax, ay, seen)
  if (seen[by * world.size + bx] !== 0) return
  carve(world, ax, ay, bx, by)
}

/** Exposed for tests: is `b` reachable from `a` over passable cells? */
export function isReachable(world: World, ax: number, ay: number, bx: number, by: number): boolean {
  const seen = new Uint8Array(world.cells)
  flood(world, ax, ay, seen)
  return seen[by * world.size + bx] !== 0
}

/** Exposed for tests: squared cell distance to the nearest reachable field. */
export function nearestReachableFieldDistance(world: World, sx: number, sy: number): number {
  const seen = new Uint8Array(world.cells)
  flood(world, sx, sy, seen)
  let best = Infinity
  for (let i = 0; i < world.cells; i++) {
    if (world.regolith[i] <= 0 || seen[i] === 0) continue
    const cx = i % world.size
    const cy = (i / world.size) | 0
    const d = (cx - sx) * (cx - sx) + (cy - sy) * (cy - sy)
    if (d < best) best = d
  }
  return best
}
